import { useEffect, useRef } from "react";
import { DESKTOP_QUERY } from "../hooks/useMediaQuery.js";
import { getCombo, eventMatchesStep, isInputActive, SHORTCUTS_CHANGED_EVENT } from "./registry.js";
import { hasOpenOverlay } from "./overlayStack.js";

/**
 * One window keydown dispatcher for every registered shortcut.
 * - desktop only (mobile behaviour is unchanged)
 * - skipped while typing in a field (except Ctrl/Alt combos)
 * - paused while a sheet/dialog is open, unless the binding sets allowInOverlay
 * - two-key sequences ("G S") wait up to 1.2s for the second key
 */
const bindings = new Map(); // id → array of { callbackRef, options } (last registered wins)
const SEQUENCE_TIMEOUT_MS = 1200;
let pending = null; // { step, at }
let combos = {};

function refreshCombos() {
  combos = {};
  for (const id of bindings.keys()) combos[id] = getCombo(id);
}

function activeBindings() {
  const list = [];
  for (const [id, stack] of bindings) {
    const top = stack[stack.length - 1];
    if (top && !top.options.disabledRef.current && combos[id]) list.push({ id, combo: combos[id], binding: top });
  }
  return list;
}

function canFire(binding, combo, e) {
  if (hasOpenOverlay() && !binding.options.allowInOverlay) return false;
  const hasModifier = /(^|\+)(Ctrl|Alt)\+/.test(combo);
  if (isInputActive() && !hasModifier) return false;
  if (e.repeat && !hasModifier) return false;
  return true;
}

function fire(binding, e) {
  e.preventDefault();
  binding.callbackRef.current?.(e);
}

function onKeyDown(e) {
  if (e.defaultPrevented || !window.matchMedia(DESKTOP_QUERY).matches) return;
  const active = activeBindings();

  // Second key of a sequence
  if (pending && Date.now() - pending.at < SEQUENCE_TIMEOUT_MS) {
    const prefix = pending.step;
    pending = null;
    for (const { combo, binding } of active) {
      const steps = combo.split(" ");
      if (steps.length === 2 && steps[0] === prefix && eventMatchesStep(e, steps[1]) && canFire(binding, combo, e)) {
        fire(binding, e);
        return;
      }
    }
  }
  pending = null;

  for (const { combo, binding } of active) {
    const steps = combo.split(" ");
    if (!eventMatchesStep(e, steps[0]) || !canFire(binding, combo, e)) continue;
    if (steps.length === 1) {
      fire(binding, e);
      return;
    }
    pending = { step: steps[0], at: Date.now() };
    e.preventDefault();
    return;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener(SHORTCUTS_CHANGED_EVENT, refreshCombos);
}

/**
 * Binds a registry action to a callback while the component is mounted.
 * @param {string} id - action id from DEFAULT_SHORTCUTS
 * @param {(e: KeyboardEvent) => void} callback
 * @param {{ disabled?: boolean, allowInOverlay?: boolean }} [options]
 */
export function useShortcut(id, callback, { disabled = false, allowInOverlay = false } = {}) {
  const callbackRef = useRef(callback);
  const disabledRef = useRef(disabled);
  callbackRef.current = callback;
  disabledRef.current = disabled;

  useEffect(() => {
    const entry = { callbackRef, options: { disabledRef, allowInOverlay } };
    const stack = bindings.get(id) || [];
    stack.push(entry);
    bindings.set(id, stack);
    combos[id] = getCombo(id);
    return () => {
      const current = bindings.get(id) || [];
      const index = current.indexOf(entry);
      if (index !== -1) current.splice(index, 1);
      if (!current.length) bindings.delete(id);
    };
  }, [id, allowInOverlay]);
}

export default useShortcut;
